import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  FormHelperText,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface SetupGuideProps {
  open: boolean;
  onClose: () => void;
}

const SetupGuide: React.FC<SetupGuideProps> = ({ open, onClose }) => {
  const { currentUser, userProfile } = useAuth();
  
  // State for the support form
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const handleClose = () => {
    // Mark as dismissed for 24 hours when user closes (temporary dismissal)
    sessionStorage.setItem('setup_guide_dismissed', Date.now().toString());
    onClose();
  };

  const handleSkip = () => {
    // Permanently skip the setup guide - never show again
    localStorage.setItem('setup_guide_permanently_skipped', 'true');
    // Also set session dismissal for consistency
    sessionStorage.setItem('setup_guide_dismissed', Date.now().toString());
    onClose();
  };

  const handleSendMessage = async () => {
    if (!subject || !message) {
      setSnackbar({
        open: true,
        message: 'Please fill in both subject and message',
        severity: 'error'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // In a real application, this would send the message to your support system
      // For now, we'll just simulate a successful send
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSnackbar({
        open: true,
        message: 'Support message sent successfully',
        severity: 'success'
      });
      
      // Reset form
      setSubject('');
      setMessage('');
      
      // Close dialog after successful send
      setTimeout(() => {
        handleClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error sending support message:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center', 
          pb: 1 
        }}>
          <Typography variant="h6" component="div">
            Contact Support
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={handleClose} 
            aria-label="close"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Need help with your account? Our support team is ready to assist you.
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              mt: 3 
            }}>
              <FormControl fullWidth>
                <TextField
                  label="Subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  placeholder="E.g., Invoice issue, Account setup"
                />
              </FormControl>
              
              <FormControl fullWidth>
                <TextField
                  label="Message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  multiline
                  rows={4}
                  placeholder="Describe your issue or question in detail..."
                />
                <FormHelperText>
                  Please include any relevant details to help us assist you better.
                </FormHelperText>
              </FormControl>
            </Box>
          </Box>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Alternative Contact Methods:
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexGrow: 1,
                borderRadius: 2
              }}
            >
              <EmailIcon color="primary" />
              <Typography variant="body2">
                support@akmobooks.com
              </Typography>
            </Paper>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexGrow: 1,
                borderRadius: 2
              }}
            >
              <PhoneIcon color="primary" />
              <Typography variant="body2">
                +91 6363 154142
              </Typography>
            </Paper>
            
            <Button 
              variant="outlined" 
              startIcon={<WhatsAppIcon />} 
              onClick={() => window.open('https://wa.me/6363154142', '_blank')}
              sx={{ 
                p: 1.5, 
                borderRadius: 2,
                flexGrow: 1
              }}
            >
              WhatsApp Support
            </Button>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleSkip} color="inherit">
            Skip
          </Button>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendMessage} 
            variant="contained" 
            color="primary"
            disabled={loading || !subject || !message}
            endIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          elevation={6} 
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SetupGuide; 